/* eslint-disable react/jsx-no-leaked-render -- It's cleaner to have && in templates */
import type { ReactNode, HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';
import type { BaseButtonProps } from '../button';
import { Button } from '../button';
import genericError from '../../assets/lotties/generic-error.json';
import { QState } from '../q-state';
import styles from './error-state.module.css';

export interface ErrorStateProps extends HTMLAttributes<HTMLDivElement> {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  onRetry?: () => void;
  retryButtonText?: string;
  subtitle?: string;
  title: string;
  retryButtonVariant?: BaseButtonProps['variant'];
  children?: ReactNode;
}

export function ErrorState({
  headingLevel = 'h2',
  retryButtonText,
  onRetry,
  subtitle,
  title,
  retryButtonVariant = 'tertiary',
  children,
  ...props
}: ErrorStateProps): ReactNode {
  const intl = useIntl();

  const displayAction = () => {
    if (children) {
      return <QState.Actions>{children}</QState.Actions>;
    } else if (onRetry) {
      return (
        <QState.Actions>
          <Button data-test-error-state-retry onPress={onRetry} variant={retryButtonVariant}>
            {retryButtonText
              ? retryButtonText
              : intl.formatMessage({ id: 'error-state-btn-retry' })}
          </Button>
        </QState.Actions>
      );
    }
    return null;
  };

  return (
    <QState className={styles.container} data-test-error-state role="alert" {...props}>
      <QState.Illustration data-testid="lottie" lottiePath={genericError} size="medium" />
      <QState.Title as={headingLevel} className="title-2 mb-8">
        {title}
      </QState.Title>
      {subtitle && <QState.Legend>{subtitle}</QState.Legend>}
      {displayAction()}
    </QState>
  );
}
